/**
 * Global Search component — renders in the top toolbar.
 *
 * Talks to the unified backend search API, which scopes every result to the
 * current user (company scoping, group privacy, own-tickets-only, etc.):
 *   - GET /api/search                      fast local content + people + apps + pages
 *   - GET /api/search/tickets              the user's own helpdesk tickets (proxy)
 *   - GET /api/search/external-documents   HR / IT / Marketing / etc. docs (proxy)
 *
 * The three calls fire in parallel; local results paint immediately and the
 * cross-app sections merge in as they resolve, so a slow/offline upstream
 * never blocks the core experience. Supports Ctrl+K / Cmd+K and arrow-key
 * navigation.
 */
import { api, router, debounce, getIcon, escapeHtml } from '@isg/ui-kit';
import { t } from '../i18n/index.js';

// Display order for the merged sections coming back from the three endpoints.
const SECTION_ORDER = [
  'people', 'tickets', 'announcements', 'documents', 'external-documents',
  'events', 'polls', 'groups', 'apps', 'pages',
];

// A function, not a module-scope const: t() must run after the locale is set.
const startHtml = () =>
  '<div class="global-search-empty"><p style="color:var(--text-tertiary);font-size:var(--text-sm);">'
  + `${t('search.start_hint')}</p></div>`;

/**
 * Create the global search element.
 * @returns {HTMLElement}
 */
export function createGlobalSearch() {
  const wrapper = document.createElement('div');
  wrapper.className = 'global-search';

  wrapper.innerHTML = `
    <div class="global-search-trigger" id="global-search-trigger" tabindex="0" role="button" aria-label="${t('common.search')}">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <span class="global-search-placeholder">${t('search.placeholder')}</span>
    </div>
    <div class="global-search-overlay" id="global-search-overlay" style="display:none;">
      <div class="global-search-modal">
        <div class="global-search-input-wrapper">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input type="text" class="global-search-input" id="global-search-input"
                 placeholder="${t('search.placeholder_full')}"
                 autocomplete="off" spellcheck="false" role="combobox" aria-expanded="true" aria-controls="global-search-results" />
          <button type="button" class="global-search-close" id="global-search-close" aria-label="${t('search.close')}" title="${t('search.close_esc')}">
            ${getIcon('x', 18)}
          </button>
        </div>
        <div class="global-search-results" id="global-search-results" role="listbox">
          ${startHtml()}
        </div>
      </div>
    </div>
  `;

  // Platform-aware keyboard-shortcut hint, surfaced as a tooltip on the
  // trigger instead of a visible badge. The Ctrl/Cmd+K shortcut still works.
  const isMac = navigator.platform?.toUpperCase().includes('MAC');
  const shortcutLabel = isMac ? 'Command+K' : 'Ctrl+K';  // i18n-ignore: keyboard key names

  const trigger = wrapper.querySelector('#global-search-trigger');
  trigger.title = t('search.trigger_shortcut', { shortcut: shortcutLabel });
  trigger.setAttribute('aria-label', t('search.trigger_shortcut', { shortcut: shortcutLabel }));
  const overlay = wrapper.querySelector('#global-search-overlay');
  const input = wrapper.querySelector('#global-search-input');
  const results = wrapper.querySelector('#global-search-results');

  let isOpen = false;
  let searchSeq = 0;       // guards against out-of-order responses
  let selectedIndex = -1;  // keyboard selection within current results

  function open() {
    isOpen = true;
    overlay.style.display = 'flex';
    requestAnimationFrame(() => {
      overlay.classList.add('active');
      input.focus();
    });
  }

  function close() {
    isOpen = false;
    searchSeq++;  // invalidate any in-flight searches
    overlay.classList.remove('active');
    setTimeout(() => {
      overlay.style.display = 'none';
      input.value = '';
      selectedIndex = -1;
      results.innerHTML = startHtml();
    }, 200);
  }

  /** True when `url` resolves to an origin other than this one. */
  function isOffOrigin(url) {
    try {
      return new URL(url, window.location.origin).origin !== window.location.origin;
    } catch (e) {
      return false;  // unparseable → treat as an in-app path, router decides
    }
  }

  function activate(el) {
    if (!el || !el.dataset.url) return;
    const url = el.dataset.url;
    if (el.dataset.external === '1') {
      // Leaves the estate (a third-party app row) or is a cross-app document
      // preview — those keep their tab.
      window.open(url, '_blank', 'noopener');
      close();
      return;
    }
    close();
    // 🔴 An INTERNAL app is still on its own origin, and this router is
    // pushState-only (ui-kit utils/router.js) — hand it
    // "https://quality.isgroup-international.com" and pushState throws a
    // SecurityError, so the result row would silently do nothing. Same
    // window, but a real navigation. Only same-origin paths reach the router.
    if (isOffOrigin(url)) { window.location.href = url; return; }
    router.navigate(url);
  }

  // ── Result rendering ────────────────────────────────────────────────

  function resultRow(item) {
    const icon = item.avatar_url
      ? `<span class="global-search-result-icon"><img class="gs-avatar" src="${escapeHtml(item.avatar_url)}" alt="" loading="lazy"></span>`
      : `<span class="global-search-result-icon">${getIcon(item.icon || 'file-text')}</span>`;
    const trailing = item.external
      ? `<span class="gs-ext" aria-hidden="true">${getIcon('external-link')}</span>` : '';
    const subtitle = item.subtitle
      ? `<div class="global-search-result-subtitle">${escapeHtml(String(item.subtitle).substring(0, 80))}</div>` : '';
    return `
      <div class="global-search-result" role="option" tabindex="-1"
           data-url="${escapeHtml(item.url || '/')}" data-external="${item.external ? '1' : '0'}">
        ${icon}
        <div class="global-search-result-text">
          <div class="global-search-result-title">${escapeHtml(item.title || t('search.untitled'))}</div>
          ${subtitle}
        </div>
        ${trailing}
      </div>`;
  }

  function renderSections(sectionMap, pending) {
    const ordered = SECTION_ORDER
      .map((k) => sectionMap[k])
      .filter((s) => s && s.items && s.items.length);

    let html = '';
    for (const sec of ordered) {
      html += `<div class="global-search-category">
        <div class="global-search-category-label">${escapeHtml(sec.label)}</div>`;
      for (const item of sec.items) html += resultRow(item);
      html += '</div>';
    }
    if (pending > 0) {
      html += '<div class="global-search-searching"><span class="spinner spinner-sm"></span> ' + t('search.searching') + '</div>';
    }
    results.innerHTML = html;
    selectedIndex = -1;
  }

  function noResults(query) {
    results.innerHTML = `
      <div class="global-search-empty">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--text-quaternary)" stroke-width="1.5">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <p style="color:var(--text-tertiary);font-size:var(--text-sm);margin-top:var(--space-2);">${t('search.no_results_for', { query: escapeHtml(query) })}</p>
      </div>`;
  }

  // ── Search ──────────────────────────────────────────────────────────

  const performSearch = debounce((query) => {
    if (!query || query.length < 2) {
      results.innerHTML = startHtml();
      return;
    }

    const seq = ++searchSeq;
    const sectionMap = {};
    let pending = 3;
    let received = false;

    results.innerHTML = '<div class="global-search-searching" style="padding:var(--space-6);justify-content:center;"><span class="spinner"></span></div>';

    const handle = (data) => {
      if (seq !== searchSeq) return;
      for (const sec of (data && data.sections) || []) {
        if (sec.items && sec.items.length) sectionMap[sec.type] = sec;
      }
      received = true;
      if (Object.keys(sectionMap).length) renderSections(sectionMap, pending);
    };

    const settle = () => {
      if (seq !== searchSeq) return;
      pending = Math.max(0, pending - 1);
      if (Object.keys(sectionMap).length) {
        renderSections(sectionMap, pending);
      } else if (pending === 0 && received) {
        noResults(query);
      }
    };

    const enc = encodeURIComponent(query);
    api.get(`/search?q=${enc}`).then(handle).catch(() => {}).finally(settle);
    api.get(`/search/tickets?q=${enc}`).then(handle).catch(() => {}).finally(settle);
    api.get(`/search/external-documents?q=${enc}`).then(handle).catch(() => {}).finally(settle);
  }, 250);

  // ── Events ──────────────────────────────────────────────────────────

  trigger.addEventListener('click', open);
  trigger.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
  });

  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  // Actionable close button (Esc still works via the input keydown handler).
  wrapper.querySelector('#global-search-close').addEventListener('click', close);

  input.addEventListener('input', () => performSearch(input.value.trim()));

  // Keyboard navigation through results (arrows + Enter), Esc to close.
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { close(); return; }
    const rows = Array.from(results.querySelectorAll('.global-search-result'));
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!rows.length) return;
      selectedIndex = (selectedIndex + 1) % rows.length;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!rows.length) return;
      selectedIndex = (selectedIndex - 1 + rows.length) % rows.length;
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activate(rows[selectedIndex] || rows[0]);
      return;
    } else {
      return;
    }
    rows.forEach((r, i) => r.classList.toggle('is-active', i === selectedIndex));
    rows[selectedIndex]?.scrollIntoView({ block: 'nearest' });
  });

  // Global Ctrl+K / Cmd+K
  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      if (isOpen) { close(); } else { open(); }
    }
  });

  // Click on a result
  results.addEventListener('click', (e) => {
    activate(e.target.closest('.global-search-result'));
  });

  // Hover updates the keyboard selection so the two stay in sync
  results.addEventListener('mousemove', (e) => {
    const row = e.target.closest('.global-search-result');
    if (!row) return;
    const rows = Array.from(results.querySelectorAll('.global-search-result'));
    const idx = rows.indexOf(row);
    if (idx !== selectedIndex) {
      selectedIndex = idx;
      rows.forEach((r, i) => r.classList.toggle('is-active', i === selectedIndex));
    }
  });

  return wrapper;
}
